import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import LocationSelector from "@/components/location/LocationSelector";
import LocationPicker from "@/components/property/LocationPicker";
import NearbyAuto from "@/components/property/NearbyAuto";
import AmenitySelector from "@/components/property/AmenitySelector";

const PROPERTY_TYPES = [
  { value: "kos", label: "Kos" },
  { value: "apartemen", label: "Apartemen" },
  { value: "kontrakan", label: "Kontrakan" },
  { value: "coliving", label: "Co-living" },
  { value: "lainnya", label: "Lainnya" },
];

const GENDERS = [
  { value: "putra", label: "Putra" },
  { value: "putri", label: "Putri" },
  { value: "campur", label: "Campur" },
  { value: "tidak_berlaku", label: "Tidak berlaku" },
];

function Field({ label, required, error, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-xs text-destructive" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors";

export default function PropertyForm({ mode = "create", property = null }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    name: property?.name || "",
    description: property?.description || "",
    property_type: property?.property_type || "",
    gender_type: property?.gender_type || "",
    address: property?.location?.address || "",
    postal_code: property?.location?.postal_code || "",
    latitude: property?.latitude ?? "",
    longitude: property?.longitude ?? "",
  });
  const [loc, setLoc] = useState({
    province: property?.location?.province_id || "",
    city: property?.location?.city_id || "",
    district: property?.location?.district_id || "",
    subdistrict: property?.location?.subdistrict_id || "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [amenityIds, setAmenityIds] = useState([]);
  const [policy, setPolicy] = useState({
    room_refund_type: property?.cancellation_policy?.room_refund_type || "full",
    room_refund_rate: property?.cancellation_policy?.room_refund_rate ?? 100,
    deposit_refund_type: property?.cancellation_policy?.deposit_refund_type || "full",
    deposit_refund_rate: property?.cancellation_policy?.deposit_refund_rate ?? 100,
  });
  const setPol = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));
  const [terms, setTerms] = useState({
    house_rules: property?.agreement_terms?.house_rules || "",
    check_in_rules: property?.agreement_terms?.check_in_rules || "",
    check_out_rules: property?.agreement_terms?.check_out_rules || "",
    additional_terms: property?.agreement_terms?.additional_terms || "",
  });
  const setTerm = (k, v) => setTerms((t) => ({ ...t, [k]: v }));

  // On edit, prefill the property's current amenities
  useEffect(() => {
    if (isEdit && property?.id) {
      apiClient
        .get(`/properties/${property.id}/amenities`)
        .then((r) => setAmenityIds((r.data || []).map((a) => a.id)))
        .catch(() => {});
    }
  }, [isEdit, property?.id]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Nama property wajib diisi";
    if (!form.description.trim()) e.description = "Deskripsi wajib diisi";
    if (!form.property_type) e.property_type = "Pilih tipe property";
    if (!form.gender_type) e.gender_type = "Pilih tipe gender";
    if (!loc.province) e.province = "Pilih provinsi";
    if (!loc.city) e.city = "Pilih kota/kabupaten";
    if (!loc.district) e.district = "Pilih kecamatan";
    if (!loc.subdistrict) e.subdistrict = "Pilih kelurahan/desa";
    if (!form.address.trim()) e.address = "Alamat lengkap wajib diisi";

    const lat = parseFloat(form.latitude);
    if (form.latitude === "" || Number.isNaN(lat)) e.latitude = "Latitude wajib diisi";
    else if (lat < -90 || lat > 90) e.latitude = "Latitude harus antara -90 dan 90";

    const lng = parseFloat(form.longitude);
    if (form.longitude === "" || Number.isNaN(lng)) e.longitude = "Longitude wajib diisi";
    else if (lng < -180 || lng > 180) e.longitude = "Longitude harus antara -180 dan 180";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError("");
    if (!validate()) {
      setFormError("Mohon lengkapi field yang wajib diisi.");
      return;
    }
    setLoading(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      property_type: form.property_type,
      gender_type: form.gender_type,
      province_id: loc.province,
      city_id: loc.city,
      district_id: loc.district,
      subdistrict_id: loc.subdistrict,
      address: form.address.trim(),
      postal_code: form.postal_code.trim() || null,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      cancellation_policy: {
        room_refund_type: policy.room_refund_type,
        room_refund_rate: policy.room_refund_type === "partial" ? Number(policy.room_refund_rate) || 0 : (policy.room_refund_type === "full" ? 100 : 0),
        deposit_refund_type: policy.deposit_refund_type,
        deposit_refund_rate: policy.deposit_refund_type === "partial" ? Number(policy.deposit_refund_rate) || 0 : (policy.deposit_refund_type === "full" ? 100 : 0),
      },
      agreement_terms: {
        house_rules: terms.house_rules.trim() || null,
        check_in_rules: terms.check_in_rules.trim() || null,
        check_out_rules: terms.check_out_rules.trim() || null,
        additional_terms: terms.additional_terms.trim() || null,
      },
    };
    try {
      if (isEdit) {
        await apiClient.put(`/properties/${property.id}`, payload);
        await apiClient.put(`/properties/${property.id}/amenities`, { amenity_ids: amenityIds });
        toast.success("Perubahan property tersimpan");
      } else {
        const { data: created } = await apiClient.post("/properties", payload);
        if (amenityIds.length) {
          await apiClient.put(`/properties/${created.id}/amenities`, { amenity_ids: amenityIds });
        }
        toast.success("Property berhasil dibuat (status: draft)");
      }
      navigate("/owner/properties");
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="property-form">
      {formError && (
        <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="property-form-error">
          {formError}
        </div>
      )}

      {/* INFORMASI */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-heading text-lg font-semibold">Informasi</h3>
        <Field label="Nama Property" required error={errors.name} testid="field-name">
          <input className={inputCls} data-testid="property-name-input" value={form.name}
            onChange={(e) => set("name", e.target.value)} placeholder="Contoh: Kos Melati Tembalang" />
        </Field>
        <Field label="Deskripsi" required error={errors.description} testid="field-description">
          <textarea rows={4} className={inputCls} data-testid="property-description-input" value={form.description}
            onChange={(e) => set("description", e.target.value)} placeholder="Deskripsikan hunian Anda..." />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Tipe Property" required error={errors.property_type} testid="field-type">
            <select className={inputCls} data-testid="property-type-select" value={form.property_type}
              onChange={(e) => set("property_type", e.target.value)}>
              <option value="">Pilih tipe</option>
              {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Tipe Gender" required error={errors.gender_type} testid="field-gender">
            <select className={inputCls} data-testid="property-gender-select" value={form.gender_type}
              onChange={(e) => set("gender_type", e.target.value)}>
              <option value="">Pilih gender</option>
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
        </div>
      </section>

      {/* LOCATION */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-heading text-lg font-semibold">Lokasi</h3>
        <LocationSelector
          onChange={setLoc}
          initialValue={isEdit ? {
            province: property?.location?.province_id,
            city: property?.location?.city_id,
            district: property?.location?.district_id,
            subdistrict: property?.location?.subdistrict_id,
          } : undefined}
        />
        {(errors.province || errors.city || errors.district || errors.subdistrict) && (
          <p className="text-xs text-destructive" data-testid="location-error">
            Lengkapi seluruh pilihan lokasi (provinsi s/d kelurahan).
          </p>
        )}
        <Field label="Alamat Lengkap" required error={errors.address} testid="field-address">
          <input className={inputCls} data-testid="property-address-input" value={form.address}
            onChange={(e) => set("address", e.target.value)} placeholder="Jl. ..., RT/RW, patokan" />
        </Field>
        <Field label="Kode Pos" testid="field-postal">
          <input className={inputCls} data-testid="property-postalcode-input" value={form.postal_code}
            onChange={(e) => set("postal_code", e.target.value)} placeholder="Opsional" />
        </Field>
      </section>

      {/* KOORDINAT (Peta) */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="coordinate-section">
        <div>
          <h3 className="font-heading text-lg font-semibold">Koordinat Lokasi</h3>
          <p className="text-sm text-muted-foreground">
            Tentukan titik lokasi properti pada peta (klik/geser pin), atau ketik latitude/longitude di bawah — peta akan mengikuti.
          </p>
        </div>

        <LocationPicker
          lat={form.latitude}
          lng={form.longitude}
          hasAddress={Boolean(form.address.trim() && loc.city)}
          addressParts={{
            address: form.address.trim(),
            subdistrict: loc.subdistrict_name || "",
            district: loc.district_name || "",
            city: loc.city_name || "",
            province: loc.province_name || "",
          }}
          onChange={(lat, lng) => {
            set("latitude", lat);
            set("longitude", lng);
          }}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Latitude" required error={errors.latitude} testid="field-lat">
            <input type="text" inputMode="decimal"
              className={inputCls}
              data-testid="property-latitude-input"
              value={form.latitude === "" || form.latitude === null || form.latitude === undefined ? "" : form.latitude}
              onChange={(e) => set("latitude", e.target.value)}
              placeholder="mis. -6.2088 atau pilih di peta" />
          </Field>
          <Field label="Longitude" required error={errors.longitude} testid="field-lng">
            <input type="text" inputMode="decimal"
              className={inputCls}
              data-testid="property-longitude-input"
              value={form.longitude === "" || form.longitude === null || form.longitude === undefined ? "" : form.longitude}
              onChange={(e) => set("longitude", e.target.value)}
              placeholder="mis. 106.8456 atau pilih di peta" />
          </Field>
        </div>
      </section>

      {/* KEBIJAKAN PEMBATALAN */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="cancellation-policy-section">
        <div>
          <h3 className="font-heading text-lg font-semibold">Kebijakan Pembatalan & Refund</h3>
          <p className="text-sm text-muted-foreground">
            Tentukan berapa persen yang dikembalikan jika customer membatalkan booking. Kebijakan ini
            disalin (snapshot) ke setiap booking saat dibuat.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground">Refund Harga Kamar</label>
            <select className={inputCls} data-testid="policy-room-type-select" value={policy.room_refund_type}
              onChange={(e) => setPol("room_refund_type", e.target.value)}>
              <option value="full">Full Refund (100%)</option>
              <option value="partial">Partial Refund (%)</option>
              <option value="non_refundable">Non-Refundable (0%)</option>
            </select>
            {policy.room_refund_type === "partial" && (
              <input type="number" min={0} max={100} className={`${inputCls} mt-2`} data-testid="policy-room-rate-input"
                value={policy.room_refund_rate} onChange={(e) => setPol("room_refund_rate", e.target.value)}
                placeholder="Persentase refund (0-100)" />
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Refund Deposit</label>
            <select className={inputCls} data-testid="policy-deposit-type-select" value={policy.deposit_refund_type}
              onChange={(e) => setPol("deposit_refund_type", e.target.value)}>
              <option value="full">Full Refund (100%)</option>
              <option value="partial">Partial Refund (%)</option>
              <option value="non_refundable">Non-Refundable (0%)</option>
            </select>
            {policy.deposit_refund_type === "partial" && (
              <input type="number" min={0} max={100} className={`${inputCls} mt-2`} data-testid="policy-deposit-rate-input"
                value={policy.deposit_refund_rate} onChange={(e) => setPol("deposit_refund_rate", e.target.value)}
                placeholder="Persentase refund (0-100)" />
            )}
          </div>
        </div>
      </section>

      {/* KETENTUAN PROPERTY (RENTAL AGREEMENT) */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="agreement-terms-section">
        <div>
          <h3 className="font-heading text-lg font-semibold">Ketentuan Property (Rental Agreement)</h3>
          <p className="text-sm text-muted-foreground">
            Ketentuan ini akan muncul pada Perjanjian Sewa dan di-snapshot saat agreement dibuat.
            Semua opsional.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Peraturan Property (House Rules)" testid="field-house-rules">
            <textarea rows={3} className={inputCls} data-testid="terms-house-rules-input" value={terms.house_rules}
              onChange={(e) => setTerm("house_rules", e.target.value)} placeholder="mis. tidak boleh berisik setelah 22.00" />
          </Field>
          <Field label="Ketentuan Tambahan" testid="field-additional-terms">
            <textarea rows={3} className={inputCls} data-testid="terms-additional-input" value={terms.additional_terms}
              onChange={(e) => setTerm("additional_terms", e.target.value)} placeholder="ketentuan lain dari pemilik" />
          </Field>
          <Field label="Ketentuan Check-in" testid="field-checkin">
            <textarea rows={2} className={inputCls} data-testid="terms-checkin-input" value={terms.check_in_rules}
              onChange={(e) => setTerm("check_in_rules", e.target.value)} placeholder="mis. check-in mulai 14.00" />
          </Field>
          <Field label="Ketentuan Check-out" testid="field-checkout">
            <textarea rows={2} className={inputCls} data-testid="terms-checkout-input" value={terms.check_out_rules}
              onChange={(e) => setTerm("check_out_rules", e.target.value)} placeholder="mis. check-out sebelum 12.00" />
          </Field>
        </div>
      </section>

      {/* FASILITAS PROPERTY */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="amenities-section">
        <div>
          <h3 className="font-heading text-lg font-semibold">Fasilitas Property</h3>
          <p className="text-sm text-muted-foreground">Pilih fasilitas yang tersedia. Bisa diubah kapan saja.</p>
        </div>
        <AmenitySelector selected={amenityIds} onChange={setAmenityIds} />
      </section>

      {/* TEMPAT TERDEKAT (otomatis) */}
      <section className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="nearby-section">
        <h3 className="font-heading text-lg font-semibold">Tempat Terdekat</h3>
        {isEdit ? (
          <NearbyAuto propertyId={property.id} canRetry />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="nearby-create-hint">
            AVASA akan otomatis menampilkan tempat penting di sekitar lokasi property setelah disimpan —
            Anda tidak perlu memilih tempat secara manual.
          </p>
        )}
      </section>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={loading}
          data-testid="property-submit-button"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Buat Property"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/owner/properties")}
          className="px-6 py-3 rounded-full border border-border text-primary font-semibold hover:bg-secondary/60 transition-colors"
          data-testid="property-cancel-button"
        >
          Batal
        </button>
      </div>
    </form>
  );
}
